import type { CSSProperties, FC, ReactNode } from 'react'
import type { GoodsItem } from '@/types'

import { useRef } from 'react'
import { ArrowUpRight, ShoppingBag } from 'lucide-react'
import {
    gsap,
    MOTION_QUERIES,
    primeOnRefresh,
    useGSAP
} from '@/lib/motion'
import SectionHeading from '@/components/landing/section-heading'
import MediaImage from '@/components/landing/media-image'
import Sparkle from '@/components/landing/decor/sparkle'
import MahoniaSprig from '@/components/landing/decor/mahonia'
import PatternArt from '@/components/landing/decor/pattern-art'
import { GOODS_COPY } from '@/constants/content'

interface GoodsSectionProps {
    goods: Array<GoodsItem>
}

interface GoodsCardProps {
    item: GoodsItem
}

const STACK_ROTATIONS = [-7, 5, -3, 8, -5, 4, -6, 3]
const REST_ROTATIONS = [-1.5, 1, -0.5, 1.5, -1, 0.5, -1.2, 0.8]

const GoodsCard: FC<GoodsCardProps> = ({ item }): ReactNode => {
    return (
        <article className='group/card relative flex h-full flex-col rounded-[10px] bg-[#fdfbf6] p-2.5 shadow-[0_26px_50px_-30px_rgba(29,42,51,0.55)] ring-1 ring-ink/10'>
            <div className='relative aspect-square overflow-hidden rounded-[6px] bg-paper'>
                {item.image
                    ? (
                        <MediaImage
                            src={item.image}
                            alt={item.imageAlt ?? item.name}
                            sizes='(min-width: 1024px) 16vw, 46vw'
                            className='object-cover object-[50%_28%] transition-transform duration-[1200ms] ease-[var(--ease-cinema)] group-hover/card:scale-[1.05]'
                        />
                    )
                    : (
                        <div className='absolute inset-0'>
                            <PatternArt seed={item.id} className='absolute inset-0 h-full w-full' />
                            <span className='absolute inset-0 m-auto flex size-12 items-center justify-center rounded-full bg-mist/85 shadow-sm ring-1 ring-ink/10'>
                                <ShoppingBag className='size-5 text-ink/50' />
                            </span>
                        </div>
                    )}
                {item.badge && (
                    <span
                        data-mdlfy-cms={`goods:${item.id}:badge`}
                        className='absolute left-2.5 top-2.5 rounded-full bg-gold px-2.5 py-1 font-mono text-[9px] font-medium tracking-[0.2em] text-ink shadow-sm'
                    >
                        {item.badge}
                    </span>
                )}
            </div>

            <div className='flex flex-1 flex-col gap-1.5 px-1.5 pb-1.5 pt-4'>
                {item.category && (
                    <p data-mdlfy-cms={`goods:${item.id}:category`} className='font-mono text-[9px] tracking-[0.22em] text-ink-soft'>
                        {item.category}
                    </p>
                )}
                <h3 data-mdlfy-cms={`goods:${item.id}:name`} className='line-clamp-2 min-h-[3.2em] text-sm leading-[1.6] tracking-[0.04em]'>
                    {item.name}
                </h3>
                {item.description && (
                    <p data-mdlfy-cms={`goods:${item.id}:description`} className='line-clamp-2 min-h-[3.6em] lg:[@media(max-height:820px)]:hidden text-xs leading-[1.8] tracking-[0.03em] text-ink/70'>
                        {item.description}
                    </p>
                )}
                <div className='mt-auto flex items-end justify-between gap-2 pt-3'>
                    {item.price
                        ? <p data-mdlfy-cms={`goods:${item.id}:price`} className='font-heading text-xl leading-none tracking-[0.02em] lg:text-2xl'>{item.price}</p>
                        : <span />}
                    {item.shopUrl
                        ? (
                            <a
                                href={item.shopUrl}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='inline-flex items-center gap-1 rounded-full px-1 py-1 text-xs tracking-[0.1em] underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-gold'
                            >
                                {GOODS_COPY.shop}
                                <ArrowUpRight className='size-3.5' />
                                <span className='sr-only'>（{item.name}、新しいタブで開きます）</span>
                            </a>
                        )
                        : <span className='font-mono text-[10px] tracking-[0.2em] text-ink-soft'>{GOODS_COPY.soon}</span>}
                </div>
            </div>
        </article>
    )
}

// GOODS: on desktop the cards appear one at a time, stacked and overlapping in the middle of the
// screen, then spread out into their places as the visitor keeps scrolling.
const GoodsSection: FC<GoodsSectionProps> = ({ goods }): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)

    useGSAP(() => {
        const section = sectionRef.current

        if (!section) return

        const q = gsap.utils.selector(section)
        const mm = gsap.matchMedia()

        const revealHeading = (blur: boolean): void => {
            gsap.timeline({ scrollTrigger: { trigger: section, start: 'top 72%' } })
                .from(q('[data-heading-meta]'), { autoAlpha: 0, x: -16, duration: 1, stagger: 0.12, ease: 'power2.out' })
                .from(q('[data-char]'), { autoAlpha: 0, y: 40, filter: blur ? 'blur(8px)' : 'blur(0px)', duration: 1.4, stagger: 0.08, ease: 'power3.out' }, 0)
                .from(q('[data-goods-lead]'), { autoAlpha: 0, y: 20, duration: 1.2, ease: 'power2.out' }, 0.4)

            gsap.fromTo(q('[data-goods-plant]'), { xPercent: -45, rotate: 16, autoAlpha: 0 }, {
                xPercent: 0,
                rotate: 6,
                autoAlpha: 1,
                ease: 'power2.out',
                scrollTrigger: { trigger: section, start: 'top 85%', end: 'top 15%', scrub: 1 }
            })
        }

        mm.add(MOTION_QUERIES.desktop, () => {
            const list = q('[data-goods="cards"]')[0]
            const cards = q('[data-goods-card]')

            if (!list || cards.length === 0) {
                revealHeading(true)

                return
            }

            section.setAttribute('data-enhanced', '')
            revealHeading(true)

            const count = cards.length
            const dx = (card: HTMLElement): number => list.offsetWidth / 2 - (card.offsetLeft + card.offsetWidth / 2)
            const dy = (card: HTMLElement): number => list.offsetHeight / 2 - (card.offsetTop + card.offsetHeight / 2)

            gsap.set(cards, { transformOrigin: '50% 60%' })

            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: section,
                    start: 'top top',
                    end: 'bottom bottom',
                    scrub: 1,
                    invalidateOnRefresh: true,
                    onRefresh: primeOnRefresh
                }
            })

            cards.forEach((card, index) => {
                tl.fromTo(
                    card,
                    {
                        x: () => dx(card) + (index - (count - 1) / 2) * 16,
                        y: () => dy(card) + 90,
                        rotate: STACK_ROTATIONS[index % STACK_ROTATIONS.length],
                        scale: 0.92,
                        autoAlpha: 0
                    },
                    {
                        y: () => dy(card) + (index % 2 === 0 ? -6 : 6),
                        scale: 1,
                        autoAlpha: 1,
                        duration: 0.5,
                        ease: 'power2.out'
                    },
                    index * 0.38
                )
            })

            tl.to(cards, {
                x: 0,
                y: 0,
                rotate: (index: number) => REST_ROTATIONS[index % REST_ROTATIONS.length],
                duration: 1.1,
                ease: 'power3.inOut',
                stagger: 0.05
            }, count * 0.38 + 0.25)
                .to({}, { duration: 0.4 })

            return () => section.removeAttribute('data-enhanced')
        })

        mm.add(MOTION_QUERIES.mobile, () => {
            revealHeading(false)

            q('[data-goods-card]').forEach((card, index) => {
                gsap.from(card, {
                    autoAlpha: 0,
                    y: 60,
                    rotate: STACK_ROTATIONS[index % STACK_ROTATIONS.length] * 0.5,
                    duration: 1.3,
                    ease: 'power3.out',
                    scrollTrigger: { trigger: card, start: 'top 90%' }
                })
            })
        })
    }, { scope: sectionRef, dependencies: [goods.length] })

    return (
        <section
            id='goods'
            ref={sectionRef}
            className='group relative overflow-hidden bg-[linear-gradient(180deg,#efd6b8_0%,#eadbd5_42%,#dcd8e4_100%)] data-[enhanced]:h-[300vh] data-[enhanced]:overflow-visible'
        >
            {/* Desktop keeps the top 6rem clear for the fixed header and the YouTube card. */}
            <div className='relative py-28 group-data-[enhanced]:sticky group-data-[enhanced]:top-0 group-data-[enhanced]:flex group-data-[enhanced]:h-svh group-data-[enhanced]:flex-col group-data-[enhanced]:justify-center group-data-[enhanced]:overflow-hidden group-data-[enhanced]:pb-0 group-data-[enhanced]:pt-24'>
                <p
                    aria-hidden='true'
                    className='pointer-events-none absolute -bottom-[4vw] right-0 whitespace-nowrap font-heading text-[30vw] font-light italic leading-none text-transparent [-webkit-text-stroke:1px_rgba(29,42,51,0.06)] lg:text-[15vw]'
                >
                    Goods ✦
                </p>

                <div data-goods-plant aria-hidden='true' className='pointer-events-none absolute bottom-[-8%] left-[-16vw] w-[44vw] max-w-[420px] lg:left-[-5vw] lg:w-[20vw]'>
                    <div className='origin-bottom motion-safe:animate-sway' style={{ animationDuration: '7.8s' }}>
                        <MahoniaSprig seed={71} />
                    </div>
                </div>

                <div className='relative mx-auto flex w-full max-w-7xl flex-col gap-12 px-5 lg:flex-row lg:items-center lg:gap-10 lg:px-12'>
                    <div className='flex flex-col gap-8 lg:w-[30%] lg:shrink-0'>
                        <SectionHeading
                            index={GOODS_COPY.index}
                            kicker={GOODS_COPY.kicker}
                            title={GOODS_COPY.title}
                            ja={GOODS_COPY.ja}
                            titleClassName='whitespace-nowrap text-[clamp(3rem,12vw,6.5rem)] lg:text-[clamp(3rem,min(5.2vw,11vh),6rem)]'
                        />
                        <p data-goods-lead className='max-w-md text-[15px] leading-[2] tracking-[0.05em] text-ink/80'>
                            {GOODS_COPY.lead}
                        </p>
                    </div>

                    {goods.length > 0
                        ? (
                            // Three cards per row on desktop; a shorter last row (2 of 5) stays centred.
                            <ul
                                data-goods='cards'
                                className='relative mx-auto flex w-full flex-wrap justify-center gap-4 sm:gap-5 lg:w-auto lg:max-w-[calc(var(--goods-card)*3+2.5rem)]'
                                style={{ '--goods-card': 'min(13vw, 19.5vh)' } as CSSProperties}
                            >
                                {goods.map(item => (
                                    <li key={item.id} data-goods-card className='w-[calc(50%-0.5rem)] sm:w-[calc(50%-0.625rem)] lg:w-[var(--goods-card)]'>
                                        <GoodsCard item={item} />
                                    </li>
                                ))}
                            </ul>
                        )
                        : (
                            <div className='flex flex-1 flex-col items-center gap-4 rounded-[22px] border border-dashed border-ink/20 bg-mist/60 px-6 py-16 text-center'>
                                <Sparkle className='size-6 text-gold' />
                                <p className='text-sm tracking-[0.08em] text-ink/75'>{GOODS_COPY.empty}</p>
                            </div>
                        )}
                </div>
            </div>
        </section>
    )
}

export default GoodsSection
